import { CommonToThree } from '@jscadui/format-threejs'

import { depthRange } from './depthRange.js'
import { createObjectCache } from './objectCache.js'

export function RenderThreejs({
  PerspectiveCamera,
  AmbientLight,
  HemisphereLight,
  WebGLRenderer,
  DirectionalLight,
  Scene,
  Group,
  // L11 fix: Removed unused BoxGeometry
  Vector3,
  Color, // used by both
  Matrix4, // used by CommonToThree for transforms
  // used by CommonToThree
  MeshPhongMaterial,
  LineBasicMaterial,
  BufferGeometry,
  BufferAttribute,
  Mesh,
  InstancedMesh,
  Line,
  LineSegments,
}) {
  let _scene
  let _camera
  let controls
  let renderer
  let _smooth
  // M14 fix: Remove unused variables SHADOW, _shouldRender, _lastRender
  let renderTimer
  let meshColor = new Color(1, 1, 1)

  const disposeObject = (obj3d) => {
    obj3d.geometry?.dispose?.()
    obj3d.material?.dispose?.()
  }
  const built = createObjectCache(disposeObject)
  let builtWith = null

  const groups = []
  let canvas

  const csgConvert = CommonToThree({
    MeshPhongMaterial,
    LineBasicMaterial,
    BufferGeometry,
    BufferAttribute,
    Mesh,
    InstancedMesh,
    Line,
    LineSegments,
    Color,
    Vector3,
    Matrix4,
  })

  const startRenderer = ({ canvas, cameraPosition = [180, -180, 220], cameraTarget = [0, 0, 0], bg = [1, 1, 1], lightPosition}) => {
    _camera = new PerspectiveCamera(45, 1, 1, 50000)
    _camera.up.set(0, 0, 1)
    _camera.position.set(...cameraPosition)
    _camera.lookAt(...cameraTarget)
    fitDepth(cameraPosition, cameraTarget)

    window.camera = _camera
    window.updateView = updateView

    _scene = new Scene()
    lightPosition = null
    const ambientLight = new AmbientLight(0xeeeeee, lightPosition ? 0.2 : 0.5)
    _scene.add(ambientLight)

    const hemiLight = new HemisphereLight(0xeeeedd, 0x333333, 0.5)
    hemiLight.position.set(0, 0, 2000)
    if(lightPosition) _scene.add(hemiLight)

    const directionalLight = new DirectionalLight(0xeeeef4, 0.7)
    // M14 fix: Shadows were always disabled (SHADOW was false), removed dead code
    if(lightPosition){
      directionalLight.position.set(...lightPosition)
      _scene.add(directionalLight)
    }else{
      // set pos relative to camera
      directionalLight.position.set(50,0,100)
      _camera.add(directionalLight)
      _scene.add(_camera)
    }
    setBg(bg)

    renderer = new WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, canvas })
    renderer.setPixelRatio(window.devicePixelRatio)
  }

  const tmFunc = typeof requestAnimationFrame === 'undefined' ? setTimeout : requestAnimationFrame

  function updateView(delay = 8) {
    if (renderTimer || !renderer) return
    renderTimer = tmFunc(updateAndRender, delay)
  }

  function setBg(bg = [1, 1, 1]) {
    _scene.background = new Color(...bg)
    updateView()
  }

  function _setSmooth(v){
    _smooth = v
  }

  function setMeshColor(bg = [1, 1, 1]) {
    meshColor = new Color(...bg)
    csgConvert.setDefColor(bg)
    updateView()
  }

  function updateAndRender() {
    renderTimer = null
    controls?.update()

    renderer.render(_scene, _camera)
    renderer.autoClear = false // allow to render multiple scenes one over other if needed
    // https://discourse.threejs.org/t/very-low-fps-when-using-composer-with-2-viewports-and-1-renderer/18586
    // https://webgl2fundamentals.org/webgl/lessons/webgl-multiple-views.html
    // http://jyunming-chen.github.io/tutsplus/tutsplus15.html  - top view plus 3d
    // https://codepen.io/jdrew1303/pen/poyVOyG --- BEST example
    // renderer.render(scene2, camera)
    // https://github.com/fennec-hub/ThreeOrbitControlsGizmo
    renderer.autoClear = true
  }

  function resize({ width, height }) {
    _camera.aspect = width / height
    _camera.updateProjectionMatrix()
    renderer.setSize(width, height)
    updateView()
  }

  const handlers = { setScene }

  function receiveCmd(cmd) {
    const fn = handlers[cmd.action]
    if (!fn) {
      throw new Error('no handler for type: ' + cmd.action)
    }
    fn(cmd)
  }

  function sendCmd(cmd) {
    receiveCmd(cmd)
  }

  function setCamera({ position, target }) {
    const validPosition = position?.every(v => Number.isFinite(v))
    const validTarget = target?.every(v => Number.isFinite(v))
    if (validPosition) {
      _camera.position.set(...position)
    }
    if (validTarget) {
      _camera.lookAt(...target)
    }
    if (validPosition && validTarget) fitDepth(position, target)
    updateView()
  }

  function fitDepth(position, target) {
    const range = depthRange(position, target)
    if (!range) return
    _camera.near = range.near
    _camera.far = range.far
    _camera.updateProjectionMatrix()
  }

  function getCamera() {
    const target = new Vector3(0, 0, -1)
    target.applyQuaternion(_camera.quaternion)
    return {
      position: _camera.position.toArray(),
      fov: _camera.fov,
      aspect: _camera.aspect,
      target: target.toArray(),
    }
  }

  return function JscadThreeViewer(el, { camera: _camera = {}, bg } = {}) {
    canvas = document.createElement('CANVAS')
    el.appendChild(canvas)

    const destroy = () => {
      // Cancel pending animation frame
      if (renderTimer) {
        cancelAnimationFrame(renderTimer)
        renderTimer = null
      }
      built.clear()
      // Clear scene
      groups.forEach(group => _scene?.remove(group))
      groups.length = 0
      // Dispose renderer
      renderer?.dispose?.()
      renderer = null
      // Remove canvas
      el.removeChild(canvas)
    }

    try {
      startRenderer({ canvas, bg, cameraPosition: _camera.position, cameraTarget: _camera.target })
    } catch (error) {
      destroy()
      throw error
    }

    const getViewerEnv = () => ({
      forceColors4: false,
      forceIndex: false,
      forceNormals: false,
      useInstances: false,
    })

    return {
      sendCmd,
      resize,
      destroy,
      getCamera,
      setCamera,
      camera: _camera,
      setBg,
      setMeshColor,
      setScene,
      getViewerEnv,
    }
  }

  function setScene(scene,{smooth, prepFit: _prepFit}={}) {
    groups.forEach(group => {
      _scene.remove(group)
    })
    groups.length = 0
    // Objects take the mesh color and smoothing they were built with
    built.begin(builtWith !== null && (builtWith.smooth !== smooth || builtWith.meshColor !== meshColor))
    builtWith = { smooth, meshColor }

    scene.items.forEach(item => {
      const group = new Group()
      group.jscadId = item.id
      group.ignoreBB = item.ignoreBB
      groups.push(group)
      item.items.forEach(obj => {
        const obj3d = built.get(obj, () => csgConvert(obj, { smooth, scene, meshColor }))
        if (obj3d) {
          group.add(obj3d)
        } else {
          console.error('could not convert to obj3d', obj)
        }
      })
      _scene.add(group)
    })
    built.end()

    updateView()
  }
}

// L11 fix: Removed unused _zoomCameraToSelection function (was from threejs example)